import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { sessionsAPI, photosAPI, feedbackAPI, SessionDetails, Photo, Feedback, FeedbackSummary, RevisionStatus } from '../api/client';
import './ProofViewer.css';

/**
 * Format expiration for customer display
 */
const formatExpirationCustomer = (expiresAt: string | null, expirationEnabled: boolean): { text: string; isUrgent: boolean } | null => {
  if (!expirationEnabled || !expiresAt) {
    return null;
  }

  const expires = new Date(expiresAt);
  const now = new Date();
  const diffMs = expires.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs <= 0) {
    return { text: 'This session has expired', isUrgent: true };
  }

  if (diffDays <= 1) {
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return { text: `Expires in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`, isUrgent: true };
  }

  if (diffDays <= 3) {
    return { text: `Expires in ${diffDays} days`, isUrgent: true };
  }

  return { text: `Expires ${expires.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, isUrgent: false };
};

/**
 * ProofViewer - Customer proof review page
 *
 * Displays photos one at a time with approve/reject buttons.
 * Supports keyboard navigation (arrow keys).
 */
const ProofViewer = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get initial photo index from query param (0-based)
  const initialPhotoParam = searchParams.get('photo');
  const initialPhotoIndex = initialPhotoParam ? parseInt(initialPhotoParam, 10) : null;

  // State
  const [session, setSession] = useState<SessionDetails | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [feedback, setFeedback] = useState<Record<string, Feedback>>({});
  const [revisionStatuses, setRevisionStatuses] = useState<Record<string, RevisionStatus>>({});
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);

  // Load session data
  useEffect(() => {
    const loadSession = async () => {
      if (!sessionId) {
        setError('Invalid session link');
        setLoading(false);
        return;
      }

      try {
        const { session: sessionData, revision_statuses } = await sessionsAPI.get(sessionId);
        setSession(sessionData);
        setPhotos(sessionData.photos || []);
        setRevisionStatuses(revision_statuses || {});

        // Convert feedback array/object to record
        if (sessionData.feedback) {
          if (Array.isArray(sessionData.feedback)) {
            const feedbackMap: Record<string, Feedback> = {};
            sessionData.feedback.forEach((fb: Feedback) => {
              feedbackMap[fb.photo_id] = fb;
            });
            setFeedback(feedbackMap);
          } else {
            setFeedback(sessionData.feedback);
          }
        }

        // Get feedback summary
        const { summary: summaryData } = await feedbackAPI.getSummary(sessionId);
        setSummary(summaryData);

        // Set initial photo index:
        // 1. Use query param if valid
        // 2. Otherwise find first photo without feedback
        // 3. Default to 0
        const photoCount = (sessionData.photos || []).length;
        if (initialPhotoIndex !== null && initialPhotoIndex >= 0 && initialPhotoIndex < photoCount) {
          setCurrentIndex(initialPhotoIndex);
        } else {
          const firstPending = (sessionData.photos || []).findIndex(
            (p: Photo) => !sessionData.feedback?.[p.id]
          );
          if (firstPending >= 0) {
            setCurrentIndex(firstPending);
          }
        }
      } catch (err: any) {
        console.error('Failed to load session:', err);
        if (err.response?.status === 410) {
          setError('This proof session has expired. Please contact Seraphina Grey for assistance.');
        } else {
          setError('Unable to load proof session. Please check your link and try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, [sessionId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (submitting || showFeedbackInput) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          if (!currentPhotoFeedback) {
            handleApprove();
          }
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          if (!currentPhotoFeedback) {
            setShowFeedbackInput(true);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, photos, submitting, showFeedbackInput]);

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowFeedbackInput(false);
      setFeedbackText('');
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < photos.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowFeedbackInput(false);
      setFeedbackText('');
    } else if (summary && summary.pending_count === 0) {
      // All photos reviewed, go to summary
      navigate(`/proof/${sessionId}/summary`);
    }
  }, [currentIndex, photos.length, summary, sessionId, navigate]);

  // Current photo and its feedback/revision status
  const currentPhoto = photos[currentIndex];
  const currentPhotoFeedback = currentPhoto ? feedback[currentPhoto.id] : null;
  const currentRevisionStatus = currentPhoto ? revisionStatuses[currentPhoto.id] : null;

  // Submit feedback
  const submitFeedback = async (status: 'approve' | 'needs_changes' | 'rejected', text: string = '') => {
    if (!sessionId || !currentPhoto || submitting) return;

    setSubmitting(true);
    try {
      const { feedback: newFeedback, summary: newSummary } = await feedbackAPI.submit(
        sessionId,
        currentPhoto.id,
        {
          approval_status: status,
          feedback_text: text,
        }
      );

      // Update local state
      setFeedback(prev => ({ ...prev, [currentPhoto.id]: newFeedback }));
      setSummary(newSummary);
      setFeedbackText('');
      setShowFeedbackInput(false);

      // Auto-advance to next photo or summary
      if (currentIndex < photos.length - 1) {
        setTimeout(() => {
          setCurrentIndex(currentIndex + 1);
        }, 300);
      } else if (newSummary.pending_count === 0) {
        setTimeout(() => {
          navigate(`/proof/${sessionId}/summary`);
        }, 500);
      }
    } catch (err: any) {
      console.error('Failed to submit feedback:', err);
      if (err.response?.status === 409) {
        setError('Feedback already submitted for this photo');
      } else if (err.response?.status === 403 && err.response?.data?.code === 'REVISION_LIMIT_REACHED') {
        setError('Maximum revision requests reached for this photo. Please approve or contact your designer.');
        setShowFeedbackInput(false);
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = () => {
    submitFeedback('approve');
  };

  const handleNeedsChanges = () => {
    submitFeedback('needs_changes', feedbackText);
  };

  const handleReject = () => {
    submitFeedback('rejected');
  };

  // Loading state
  if (loading) {
    return (
      <div className="proof-viewer">
        <div className="proof-loading">
          <div className="proof-spinner"></div>
          <p>Loading your proofs...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !session) {
    return (
      <div className="proof-viewer">
        <div className="proof-error">
          <h2>Unable to Load Proofs</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No photos state
  if (photos.length === 0) {
    return (
      <div className="proof-viewer">
        <div className="proof-empty">
          <h2>No Photos Available</h2>
          <p>This proof session doesn't have any photos yet.</p>
          <p>Please check back later or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="proof-viewer">
      {/* Header */}
      <header className="proof-header">
        <div className="proof-header-content">
          <div className="proof-brand">
            <span className="brand-name">SERAPHINA GREY</span>
            <span className="brand-divider">|</span>
            <span className="brand-subtitle">PROOF REVIEW</span>
          </div>
          <div className="proof-customer">
            {session?.customer_name}
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="proof-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${((summary?.total_feedback || 0) / photos.length) * 100}%` }}
          />
        </div>
        <div className="progress-text">
          <span className="progress-count">
            {summary?.approved_count || 0} of {photos.length} approved
          </span>
          {summary && summary.pending_count === 0 && (
            <button
              className="btn-view-summary"
              onClick={() => navigate(`/proof/${sessionId}/summary`)}
            >
              View Summary
            </button>
          )}
        </div>
      </div>

      {/* Expiration notice */}
      {session && (() => {
        const expInfo = formatExpirationCustomer(session.expires_at, session.expiration_enabled);
        if (!expInfo) return null;
        return (
          <div className={`expiration-notice ${expInfo.isUrgent ? 'urgent' : ''}`}>
            <svg viewBox="0 0 20 20" fill="currentColor" className="expiration-notice-icon">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            <span>{expInfo.text}</span>
          </div>
        );
      })()}

      {/* Main content */}
      <main className="proof-main">
        {/* Photo display */}
        <div className="photo-container">
          <div className="photo-wrapper">
            <img
              src={photosAPI.getPhotoUrl(currentPhoto)}
              alt={currentPhoto.display_name || currentPhoto.original_filename}
              className="proof-photo"
            />
            {/* Version badge for updated photos */}
            {currentPhoto.version_number && currentPhoto.version_number > 1 && (
              <div className="photo-version-indicator">
                Updated v{currentPhoto.version_number}
              </div>
            )}
            {currentPhotoFeedback && (
              <div className={`photo-status-badge ${currentPhotoFeedback.approval_status}`}>
                {currentPhotoFeedback.approval_status === 'approve'
                  ? 'Approved'
                  : currentPhotoFeedback.approval_status === 'rejected'
                  ? 'Rejected'
                  : 'Changes Requested'}
              </div>
            )}
          </div>
        </div>

        {/* Photo info */}
        <div className="photo-info">
          <span className="photo-number">Photo {currentIndex + 1} of {photos.length}</span>
          <span className="photo-filename">{currentPhoto.display_name || currentPhoto.original_filename}</span>
        </div>

        {/* Revision credits display - above action buttons */}
        {!currentPhotoFeedback && currentRevisionStatus && (
          <div className="revision-info">
            <span className="revision-remaining">
              {currentRevisionStatus.max_revisions - currentRevisionStatus.current_revisions} revision{currentRevisionStatus.max_revisions - currentRevisionStatus.current_revisions !== 1 ? 's' : ''} remaining
            </span>
          </div>
        )}

        {/* Navigation and feedback */}
        <div className="proof-actions">
          {/* Navigation buttons */}
          <button
            className="btn-nav btn-prev"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            title="Previous photo (Left Arrow)"
          >
            <span className="nav-icon">&#8592;</span>
            <span className="nav-text">Previous</span>
          </button>

          {/* Feedback buttons */}
          {!currentPhotoFeedback && !showFeedbackInput && (
            <div className="feedback-buttons">
              <button
                className="btn-feedback btn-approve"
                onClick={handleApprove}
                disabled={submitting}
                title="Approve (A)"
              >
                {submitting ? 'Submitting...' : 'Approve'}
              </button>
              {currentRevisionStatus && !currentRevisionStatus.can_request_changes ? (
                <div className="revision-limit-reached">
                  <button
                    className="btn-feedback btn-reject"
                    onClick={handleReject}
                    disabled={submitting}
                    title="Reject this photo"
                  >
                    {submitting ? 'Submitting...' : 'Reject Photo'}
                  </button>
                  <p className="revision-limit-message">
                    No more revisions available. You may approve or reject this photo.
                  </p>
                </div>
              ) : (
                <button
                  className={`btn-feedback btn-changes ${currentRevisionStatus?.is_last_revision ? 'btn-danger-stripe' : ''}`}
                  onClick={() => setShowFeedbackInput(true)}
                  disabled={submitting}
                  title={currentRevisionStatus?.is_last_revision
                    ? 'This is your last revision request (R)'
                    : 'Request Changes (R)'}
                >
                  Request Changes
                  {currentRevisionStatus?.is_last_revision && (
                    <span className="last-revision-badge">Last Revision</span>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Feedback input form */}
          {!currentPhotoFeedback && showFeedbackInput && (
            <div className={`feedback-form ${currentRevisionStatus?.is_last_revision ? 'last-revision-form' : ''}`}>
              {currentRevisionStatus?.is_last_revision && (
                <div className="last-revision-warning">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="warning-icon">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>This is your final revision request for this photo</span>
                </div>
              )}
              <textarea
                className="feedback-textarea"
                placeholder="What changes would you like? (optional)"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                maxLength={1000}
                autoFocus
              />
              <div className="feedback-form-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowFeedbackInput(false);
                    setFeedbackText('');
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className={`btn-submit-changes ${currentRevisionStatus?.is_last_revision ? 'btn-danger-stripe' : ''}`}
                  onClick={handleNeedsChanges}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </div>
          )}

          {/* Already reviewed indicator */}
          {currentPhotoFeedback && (
            <div className="feedback-submitted">
              <span className={`feedback-status ${currentPhotoFeedback.approval_status}`}>
                {currentPhotoFeedback.approval_status === 'approve' ? (
                  <>&#10003; Approved</>
                ) : currentPhotoFeedback.approval_status === 'rejected' ? (
                  <>&#10007; Rejected</>
                ) : (
                  <>&#9998; Changes Requested</>
                )}
              </span>
              {currentPhotoFeedback.feedback_text && (
                <p className="feedback-note">{currentPhotoFeedback.feedback_text}</p>
              )}
            </div>
          )}

          <button
            className="btn-nav btn-next"
            onClick={goToNext}
            disabled={currentIndex === photos.length - 1 && summary?.pending_count !== 0}
            title="Next photo (Right Arrow)"
          >
            <span className="nav-text">
              {currentIndex === photos.length - 1 && summary?.pending_count === 0
                ? 'View Summary'
                : 'Next'}
            </span>
            <span className="nav-icon">&#8594;</span>
          </button>
        </div>

        {/* Hint when Next is disabled on last photo */}
        {currentIndex === photos.length - 1 && summary && summary.pending_count > 0 && (
          <div className="next-disabled-hint">
            {summary.pending_count} photo{summary.pending_count !== 1 ? 's' : ''} still need{summary.pending_count === 1 ? 's' : ''} approval
          </div>
        )}

        {/* Keyboard hints */}
        <div className="keyboard-hints">
          <span className="hint"><kbd>&#8592;</kbd> Previous</span>
          <span className="hint"><kbd>&#8594;</kbd> Next</span>
          {!currentPhotoFeedback && !showFeedbackInput && (
            <>
              <span className="hint"><kbd>A</kbd> Approve</span>
              <span className="hint"><kbd>R</kbd> Request Changes</span>
            </>
          )}
        </div>
      </main>

      {/* Photo thumbnails strip */}
      <div className="proof-thumbnails">
        <div className="thumbnails-container">
          {photos.map((photo, index) => (
            <button
              key={photo.id}
              className={`thumbnail ${index === currentIndex ? 'active' : ''} ${
                feedback[photo.id] ? feedback[photo.id].approval_status : 'pending'
              }`}
              onClick={() => {
                setCurrentIndex(index);
                setShowFeedbackInput(false);
                setFeedbackText('');
              }}
              title={`Photo ${index + 1}: ${photo.display_name || photo.original_filename}${photo.version_number && photo.version_number > 1 ? ` (v${photo.version_number})` : ''}`}
            >
              <img
                src={photosAPI.getThumbnailUrl(photo)}
                alt={`Thumbnail ${index + 1}`}
                loading="lazy"
              />
              {photo.version_number && photo.version_number > 1 && (
                <span className="thumb-version">v{photo.version_number}</span>
              )}
              {feedback[photo.id] && (
                <span className={`thumb-status ${feedback[photo.id].approval_status}`}>
                  {feedback[photo.id].approval_status === 'approve' ? '✓' : '✎'}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Toast for errors */}
      {error && session && (
        <div className="proof-toast error" onClick={() => setError(null)}>
          {error}
        </div>
      )}
    </div>
  );
};

export default ProofViewer;
